import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { CardTitleWithHelp, PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useAuth } from "@/components/AuthProvider";
import { ApiError } from "@/lib/api";
import { formatKitQuantity } from "@/lib/formatKitQuantity";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import {
  configurationLaborPageKeyForAlternate,
  configurationLaborPageKeyForAlternateOrder,
  configurationLaborPageKeyForBatchItem,
} from "@/pages/manufacturing/configurationLaborPage";
import { formatLaborLocationLabel } from "@/lib/labor/laborPageKey";
import { playPairSuccessSound } from "@/lib/floorStationPairAudio";
import { flashAndBumpToOverview } from "@/lib/labor/laborTimeoutHandlers";
import { useLaborSession } from "@/lib/labor/useLaborSession";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import {
  devicesForSingleDeviceConfig,
  extractMcuId,
  listSdTestDevices,
  SDTEST_FIELD_KEYS,
  SDTESTAPP_DEFAULT_PORT,
  SdTestAppHttpClient,
  SdTestAppHubError,
  SdTestConnectorRefreshError,
  type SdTestConnectionStatus,
  type SdTestDemographics,
  type SdTestStatus,
} from "@/lib/sdtestapp/sdTestAppHttpClient";

import {
  CONFIGURATION_JOB_PRESENCE_INTERVAL_MS,
  CONFIGURATION_JOB_SYNC_AT_ZERO_INTERVAL_MS,
  CONFIGURATION_JOB_SYNC_INTERVAL_MS,
  CONFIGURATION_QUEUE_QUERY_KEY,
  configurationActiveJobOperatorsQueryKey,
  fetchConfigurationActiveJobOperators,
  fetchConfigurationBuiltUnits,
  fetchConfigurationClampQcCheck,
  fetchConfigurationOpenQualityIncidentCheck,
  fetchConfigurationJobProgress,
  fetchConfigurationPcbaPrefixTlaCheck,
  fetchConfigurationPcbaTestCheck,
  fetchConfigurationQueue,
  fetchConfigurationRequiredTla,
  submitConfiguration,
  configurationSubmitSuccessLabel,
  type ConfigurationBuiltUnit,
  type ConfigurationJobProgress,
  type ConfigurationPcbaTestRowSummary,
  type ConfigurationQueueItem,
  type SubmitConfigurationBody,
  type SubmitConfigurationResult,
} from "./configurationApi";
import { ConfigurationBuiltUnitsPanel } from "./ConfigurationBuiltUnitsPanel";
import { DeviceLookupModalTrigger } from "./DeviceLookupModal";
import { ConfigurationDeviceForm, type ConfigurationDeviceFormHandle } from "./ConfigurationDeviceForm";
import {
  CONFIGURATION_ALREADY_ON_ORDER_BUTTON_LABEL,
  CONFIGURATION_CHECKIN_FAILED_BUTTON_LABEL,
  ConfigurationSubmitFailure,
} from "./configurationSubmitFailure";
import { ConfigurationDevicePolledPanel } from "./ConfigurationDevicePolledPanel";
import { ConfigurationJobTargets } from "./ConfigurationJobTargets";
import { ConfigurationSdTestConnectionPanel } from "./ConfigurationSdTestConnectionPanel";
import {
  configurationJobSyncRefetchIntervalMs,
  filterConfigurationOperatorsForJob,
} from "./configurationJobPresence";
import { PrePackCartonOperatorStack } from "@/pages/prepack/PrePackCartonOperatorStack";
import { resolveWirelessSimCarrier } from "@integrator-src/manufacturing/sim-iccid-carrier";
import { isKodiakConfigurationPcbaSerial } from "@integrator-src/manufacturing/configuration-pcba-serial";
import {
  getConfigurationDeviceType,
  isValidImeiForWireless,
  firmwareSupportsMcuUuid,
  deviceCommunicationFailureDetail,
  deviceCommunicationFailureMessage,
  MCU_UUID_UNREAD_DETAIL,
  missingMcuUuidWhenSupportedMessage,
  readPolledDeviceFirmware,
  readConfigurationPrefillValue,
  readPolledDeviceMcuUuid,
  readDeviceConnectorId,
  configurationMcuUuidsMatch,
  resolveConfigureSubmitField,
  skuRequiresImeiIccid,
  skipsPcbaTestRecordRequirement,
  isPolledDeviceDemographicsStale,
  configureErrorSuggestsConnectorRefresh,
  validateConfigurationIccidCarrierAlignment,
} from "./configurationDeviceFields";
import {
  configurationPreConfigQcIssueNotes,
  inferPcbaProductFamilyFromConfigureSku,
  inferPcbaTlaVariantFromTlaSku,
} from "./configurationPcbaIncidentDefaults";
import {
  configurationPcbaPrefixNoAssignmentMessage,
} from "./configurationPcbaPrefixBlock";
import {
  recordConfigurationClampQcCheckForSupport,
  recordConfigurationDeviceCommunicationFailureForSupport,
  recordConfigurationOpenQualityIncidentCheckForSupport,
  recordConfigurationPcbaPrefixTlaCheckForSupport,
} from "./configurationSupportLogContext";
import { CONFIGURATION_US_CLAMP_SCAN_VALUE } from "./configurationDeviceFields";
import { ConfigurationPreConfigQcIncidentModal } from "./ConfigurationPreConfigQcIncidentModal";
import { CONFIGURATION_LABOR_FLASH_STORAGE_KEY } from "./configurationLaborFlash";
import { configurationQueuePath } from "./configurationNavigation";

const SDTEST_HELP =
  "SDTestApp must be running on this workstation and its self-signed certificate trusted in this browser. " +
  "This page calls the local Hub REST API at https://127.0.0.1:<port>/api/hub/v1 (no iframe). Exactly one " +
  "physical device should be connected before submitting. Requires SDTestApp 0.16.2+; connector refresh requires 0.17.0+.";

const WORK_PAGE_HELP =
  "Scan or enter serial values, review polled device data from SDTestApp, then submit to program the device and " +
  "write serial_fci (and the wireless portal for cellular units). " +
  SDTEST_HELP;

function formatPcbaTestTimestamp(value: string | null): string {
  if (value == null) return "Not recorded";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function pcbaTestPassLabel(value: boolean | null | undefined): string {
  if (value == null) return "Not recorded";
  return value ? "Pass" : "Fail";
}

function pcbaTestPassTone(value: boolean | null | undefined): "success" | "danger" | "muted" {
  if (value === true) return "success";
  if (value === false) return "danger";
  return "muted";
}

function ConfigurationPcbaTestList({ tests }: { tests: ConfigurationPcbaTestRowSummary[] }): JSX.Element | null {
  if (tests.length === 0) return null;

  return (
    <div className="mt-3 space-y-2">
      <CardTitleWithHelp
        titleClassName="text-sm"
        hoverHint="Recent PCBA firmware and sleep current tests for this MCU. Configure passes when the latest firmware and latest sleep are both pass and that sleep is on or after that firmware."
      >
        PCBA tests
      </CardTitleWithHelp>
      <ul className="space-y-2">
        {tests.map((test) => (
          <li key={test.id} className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700">
            <div className="flex flex-wrap items-center gap-1">
              {test.is_latest ? <Badge tone="info">Latest</Badge> : null}
              {test.ban ? <Badge tone="danger">Banned</Badge> : null}
              <Badge tone={pcbaTestPassTone(test.power_test_pass)}>Power {pcbaTestPassLabel(test.power_test_pass)}</Badge>
              <Badge tone={pcbaTestPassTone(test.firmware_pass)}>
                Firmware {pcbaTestPassLabel(test.firmware_pass)}
              </Badge>
            </div>
            <p className="mt-1 text-slate-600">{formatPcbaTestTimestamp(test.power_test_timestamp)}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

type WorkMode = "build" | "alternate_order" | "alternate";

export function ConfigurationWorkPage(): JSX.Element {
  const params = useParams<{ batchItemId?: string; alternateOrderId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const writesAllowed = useHubModuleWrite("manufacturing");
  const { showToast } = useHubToast();

  const batchItemId = Number.parseInt(params.batchItemId ?? "", 10);
  const alternateOrderId = Number.parseInt(params.alternateOrderId ?? "", 10);
  const alternateSku = (searchParams.get("sku") ?? "").trim();
  const mode: WorkMode =
    Number.isFinite(batchItemId) && batchItemId > 0
      ? "build"
      : Number.isFinite(alternateOrderId) && alternateOrderId > 0
        ? "alternate_order"
        : "alternate";

  const laborPage = useMemo(() => {
    if (mode === "build" && Number.isFinite(batchItemId) && batchItemId > 0) {
      return configurationLaborPageKeyForBatchItem(batchItemId);
    }
    if (mode === "alternate_order" && Number.isFinite(alternateOrderId) && alternateOrderId > 0) {
      return configurationLaborPageKeyForAlternateOrder(alternateOrderId);
    }
    if (mode === "alternate" && alternateSku !== "") {
      return configurationLaborPageKeyForAlternate(alternateSku);
    }
    return "";
  }, [mode, batchItemId, alternateOrderId, alternateSku]);

  const bumpConfigurationLaborToQueueWithFlash = useCallback(
    (message: string) => {
      flashAndBumpToOverview({
        navigate,
        to: configurationQueuePath(),
        storageKey: CONFIGURATION_LABOR_FLASH_STORAGE_KEY,
        message,
        replace: true,
      });
    },
    [navigate],
  );

  const labor = useLaborSession({
    page: laborPage,
    domain: "manufacturing",
    moduleGate: "manufacturing",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: "Configuration",
    autoStart: laborPage !== "",
    requirePersistedClockIn: true,
    onIdleTimeout: () =>
      bumpConfigurationLaborToQueueWithFlash(
        "Clocked out due to inactivity on this configuration job. Open the build line or alternate again from the queue when you return.",
      ),
    onPersistedSessionLost: () =>
      bumpConfigurationLaborToQueueWithFlash(
        "Your hub labor session for this configuration job ended (timeout, error, or the session was closed on the server). Use Back to queue, then open the job again to clock back in.",
      ),
    onSessionEndedRemotely: () =>
      bumpConfigurationLaborToQueueWithFlash(
        "Clocked out from another tab or the session was closed on the server. Open the job again from the queue when you want to continue.",
      ),
    onStopped: () =>
      bumpConfigurationLaborToQueueWithFlash(
        "Clocked out. Open the job again from the queue when you want to continue.",
      ),
    localOnlyMessage: (reason) =>
      reason === "read-only"
        ? "Started working. Your role is read-only for Manufacturing, so this is a local timer only."
        : "Started working. Labor writes are blocked, so this is a local timer only.",
  });

  const laborSnapshot = useMemo((): LaborStatusPanelProps => {
    return {
      isWorking: labor.isWorking,
      elapsedSeconds: labor.elapsedSeconds,
      workLabel: "Configuration",
      banner: labor.banner,
      lastSummary: labor.lastSummary,
      heartbeatIntervalMs: labor.heartbeatIntervalMs,
      idleTimeoutMs: labor.idleTimeoutMs,
    };
  }, [
    labor.isWorking,
    labor.elapsedSeconds,
    labor.banner,
    labor.lastSummary,
    labor.heartbeatIntervalMs,
    labor.idleTimeoutMs,
  ]);

  useLaborDevStatusPanelRegistration(import.meta.env.DEV, laborSnapshot);

  const laborGateConflictBumpRef = useRef(false);
  useEffect(() => {
    if (laborPage === "") return;
    if (labor.loading) return;
    if (labor.activeOnOtherPage) {
      if (!laborGateConflictBumpRef.current) {
        laborGateConflictBumpRef.current = true;
        bumpConfigurationLaborToQueueWithFlash(
          `You are already clocked in on ${formatLaborLocationLabel(labor.activeOnOtherPage)}. Return to the queue, stop that session, then open this job again.`,
        );
      }
      return;
    }
    laborGateConflictBumpRef.current = false;
  }, [laborPage, labor.loading, labor.activeOnOtherPage, bumpConfigurationLaborToQueueWithFlash]);

  const [port, setPort] = useState<string>(SDTESTAPP_DEFAULT_PORT);
  const [status, setStatus] = useState<SdTestStatus>({ text: "SDTestApp Offline", color: "#DC3545" });
  const [connection, setConnection] = useState<SdTestConnectionStatus>("connecting");
  const [demographics, setDemographics] = useState<SdTestDemographics | null>(null);
  const httpClientRef = useRef<SdTestAppHttpClient | null>(null);
  const configurationFormRef = useRef<ConfigurationDeviceFormHandle>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectorRefreshPending, setConnectorRefreshPending] = useState(false);
  const [connectorRefreshNotice, setConnectorRefreshNotice] = useState<{
    text: string;
    tone: "error" | "warning" | "success";
  } | null>(null);
  const connectorRefreshNoticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastConnectorIdRef = useRef("");
  const [submitResult, setSubmitResult] = useState<SubmitConfigurationResult | null>(null);
  const [reconfigureError, setReconfigureError] = useState<string | null>(null);
  const [builtUnitsFilter, setBuiltUnitsFilter] = useState("");
  const [rescanSerialFciId, setRescanSerialFciId] = useState<number | null>(null);
  const [preConfigQcOpen, setPreConfigQcOpen] = useState(false);
  const [preConfigQcPcbaSerial, setPreConfigQcPcbaSerial] = useState("");
  const [preConfigQcIdentity, setPreConfigQcIdentity] = useState<{
    deviceLabelSerial: string;
    clampSerial: string;
    mcuUuid: string;
  }>({ deviceLabelSerial: "", clampSerial: "", mcuUuid: "" });

  const queryClient = useQueryClient();
  const { session } = useAuth();
  const viewerUserId = session?.user?.id ?? null;

  const queueQuery = useQuery({
    queryKey: CONFIGURATION_QUEUE_QUERY_KEY,
    queryFn: fetchConfigurationQueue,
    staleTime: 60_000,
  });

  const jobProgressEnabled =
    (mode === "build" && Number.isFinite(batchItemId) && batchItemId > 0) ||
    (mode === "alternate_order" && Number.isFinite(alternateOrderId) && alternateOrderId > 0);

  const jobPresenceEnabled =
    jobProgressEnabled || (mode === "alternate" && alternateSku !== "");

  const jobProgressQueryKey = useMemo(
    () =>
      [
        "manufacturing",
        "configuration",
        "job-progress",
        mode,
        mode === "build" ? batchItemId : null,
        mode === "alternate_order" ? alternateOrderId : null,
      ] as const,
    [mode, batchItemId, alternateOrderId],
  );

  const operatorsQuery = useQuery({
    queryKey: configurationActiveJobOperatorsQueryKey,
    queryFn: fetchConfigurationActiveJobOperators,
    enabled: jobPresenceEnabled,
    staleTime: CONFIGURATION_JOB_PRESENCE_INTERVAL_MS,
    refetchInterval: CONFIGURATION_JOB_PRESENCE_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  const operatorsOnJob = useMemo(() => {
    if (!jobPresenceEnabled) return [];
    return filterConfigurationOperatorsForJob(
      operatorsQuery.data ?? [],
      mode,
      batchItemId,
      alternateOrderId,
      alternateSku,
    );
  }, [jobPresenceEnabled, mode, batchItemId, alternateOrderId, alternateSku, operatorsQuery.data]);

  const resolveJobSyncInterval = useCallback(
    (remainingToBuild: number | null | undefined): number | false =>
      configurationJobSyncRefetchIntervalMs(
        operatorsOnJob,
        remainingToBuild,
        CONFIGURATION_JOB_SYNC_INTERVAL_MS,
        CONFIGURATION_JOB_SYNC_AT_ZERO_INTERVAL_MS,
      ),
    [operatorsOnJob],
  );

  const jobProgressQuery = useQuery({
    queryKey: jobProgressQueryKey,
    queryFn: (): Promise<ConfigurationJobProgress> =>
      mode === "build"
        ? fetchConfigurationJobProgress({ batchItemId })
        : fetchConfigurationJobProgress({ alternateOrderId }),
    enabled: jobProgressEnabled,
    staleTime: CONFIGURATION_JOB_SYNC_INTERVAL_MS,
    refetchInterval: (query) => resolveJobSyncInterval(query.state.data?.remainingToBuild),
    refetchIntervalInBackground: false,
  });

  const builtUnitsQueryKey = useMemo(
    () =>
      [
        "manufacturing",
        "configuration",
        "built-units",
        mode,
        mode === "build" ? batchItemId : null,
        mode === "alternate_order" ? alternateOrderId : null,
      ] as const,
    [mode, batchItemId, alternateOrderId],
  );

  const builtUnitsQuery = useQuery({
    queryKey: builtUnitsQueryKey,
    queryFn: () =>
      mode === "build"
        ? fetchConfigurationBuiltUnits({ batchItemId })
        : fetchConfigurationBuiltUnits({ alternateOrderId }),
    enabled:
      (mode === "build" && Number.isFinite(batchItemId) && batchItemId > 0) ||
      (mode === "alternate_order" && Number.isFinite(alternateOrderId) && alternateOrderId > 0),
    staleTime: CONFIGURATION_JOB_SYNC_INTERVAL_MS,
    refetchInterval: () => {
      const progress = queryClient.getQueryData<ConfigurationJobProgress>(jobProgressQueryKey);
      return resolveJobSyncInterval(progress?.remainingToBuild);
    },
    refetchIntervalInBackground: false,
  });

  const queueItem = useMemo<ConfigurationQueueItem | null>(() => {
    const items = queueQuery.data?.items ?? [];
    if (mode === "build" && Number.isFinite(batchItemId) && batchItemId > 0) {
      return items.find((item) => item.kind === "batch_line" && item.batchItemId === batchItemId) ?? null;
    }
    if (mode === "alternate_order" && Number.isFinite(alternateOrderId) && alternateOrderId > 0) {
      return (
        items.find((item) => item.kind === "alternate_order" && item.alternateOrderId === alternateOrderId) ?? null
      );
    }
    return null;
  }, [mode, batchItemId, alternateOrderId, queueQuery.data?.items]);

  /** Keep job context when sgs-queue drops a line after units are configured (queue refetch is not job validity). */
  const cachedQueueItemRef = useRef<ConfigurationQueueItem | null>(null);
  const jobContextReadyRef = useRef(false);
  useEffect(() => {
    cachedQueueItemRef.current = null;
    jobContextReadyRef.current = false;
  }, [mode, batchItemId, alternateOrderId]);
  if (queueItem != null) {
    cachedQueueItemRef.current = queueItem;
  }
  const activeQueueItem = queueItem ?? cachedQueueItemRef.current;
  if (
    activeQueueItem != null ||
    jobProgressQuery.data != null ||
    (builtUnitsQuery.data?.units.length ?? 0) > 0
  ) {
    jobContextReadyRef.current = true;
  }

  const targetSku =
    mode === "build" || mode === "alternate_order" ? activeQueueItem?.sku ?? "" : alternateSku;

  const deviceType = useMemo(() => getConfigurationDeviceType(targetSku), [targetSku]);

  const requiredTlaQuery = useQuery({
    queryKey: ["manufacturing", "configuration", "required-tla", targetSku] as const,
    queryFn: () => fetchConfigurationRequiredTla(targetSku),
    enabled: targetSku.trim() !== "",
    staleTime: 60_000,
  });

  const requiredTlaSku = requiredTlaQuery.data?.tlaSku ?? null;

  useEffect(() => {
    if (mode !== "build" && mode !== "alternate_order") {
      return;
    }
    if (!queueQuery.isSuccess || queueQuery.isFetching) {
      return;
    }
    if (queueItem != null || cachedQueueItemRef.current != null) {
      return;
    }
    if (jobProgressQuery.isLoading || jobProgressQuery.isFetching) {
      return;
    }
    if (jobProgressQuery.data != null) {
      return;
    }
    navigate(configurationQueuePath(), { replace: true });
  }, [
    mode,
    queueQuery.isSuccess,
    queueQuery.isFetching,
    queueItem,
    jobProgressQuery.isLoading,
    jobProgressQuery.isFetching,
    jobProgressQuery.data,
    navigate,
  ]);

  useEffect(() => {
    setBuiltUnitsFilter("");
    setRescanSerialFciId(null);
  }, [mode, batchItemId, alternateOrderId]);

  useEffect(() => {
    if (mode === "alternate" && alternateSku === "") {
      navigate(configurationQueuePath(), { replace: true });
    }
  }, [mode, alternateSku, navigate]);

  useEffect(() => {
    const client = new SdTestAppHttpClient(port, {
      onStatusChange: (next, conn) => {
        setStatus(next);
        setConnection(conn);
      },
      onDemographics: (next) => setDemographics(next),
    });
    httpClientRef.current = client;
    client.startPolling(3000);
    return () => {
      client.dispose();
      httpClientRef.current = null;
    };
  }, [port]);

  const activeDevices = useMemo(
    () => devicesForSingleDeviceConfig(listSdTestDevices(demographics)),
    [demographics],
  );

  const activeDevice = activeDevices.length === 1 ? activeDevices[0] : null;
  const activeCount = activeDevices.length;

  const polledMcuUuid = useMemo(() => readPolledDeviceMcuUuid(activeDevice), [activeDevice]);

  const connectedConnectorId = useMemo(
    () => (activeDevice ? readDeviceConnectorId(activeDevice) : ""),
    [activeDevice],
  );

  const displayedConnectorId =
    connectedConnectorId.trim() !== "" ? connectedConnectorId : lastConnectorIdRef.current;

  useEffect(() => {
    if (connectedConnectorId.trim() !== "") {
      lastConnectorIdRef.current = connectedConnectorId;
    }
  }, [connectedConnectorId]);

  useEffect(() => {
    setRescanSerialFciId(null);
  }, [connectedConnectorId]);

  const submitSuccessMessage = useMemo(
    () => (submitResult ? configurationSubmitSuccessLabel(submitResult) : null),
    [submitResult],
  );

  useEffect(() => {
    if (connection !== "connected" || activeCount === 0) {
      setSubmitResult(null);
    }
  }, [connection, activeCount]);

  const [pcbaSerialDraft, setPcbaSerialDraft] = useState("");
  const [clampSerialDraft, setClampSerialDraft] = useState("");
  const [deviceLabelSerialDraft, setDeviceLabelSerialDraft] = useState("");
  const [iccidDraft, setIccidDraft] = useState("");
  const refreshPcbaSerialDraft = useCallback(() => {
    setPcbaSerialDraft(configurationFormRef.current?.readFieldValue("pcbaSerial") ?? "");
  }, []);
  const refreshClampSerialDraft = useCallback(() => {
    setClampSerialDraft(configurationFormRef.current?.readFieldValue("clampSerial") ?? "");
  }, []);
  const refreshDeviceLabelSerialDraft = useCallback(() => {
    setDeviceLabelSerialDraft(configurationFormRef.current?.readFieldValue("deviceLabelSerial") ?? "");
  }, []);
  const refreshIccidDraft = useCallback(() => {
    setIccidDraft(configurationFormRef.current?.readFieldValue("iccid") ?? "");
  }, []);

  const pcbaPrefixCheckQuery = useQuery({
    queryKey: [
      "manufacturing",
      "configuration",
      "pcba-prefix-tla-check",
      pcbaSerialDraft,
      targetSku,
      requiredTlaSku,
    ] as const,
    queryFn: () =>
      fetchConfigurationPcbaPrefixTlaCheck({
        pcba_serial: pcbaSerialDraft.trim(),
        finished_sku: targetSku,
        required_tla_sku: requiredTlaSku ?? undefined,
      }),
    enabled: isKodiakConfigurationPcbaSerial(pcbaSerialDraft) && targetSku.trim() !== "",
    staleTime: 30_000,
  });

  const pcbaPrefixMismatch = pcbaPrefixCheckQuery.data?.matches_required_tla === false;
  const pcbaPrefixNoAssignment = pcbaPrefixCheckQuery.data?.has_prefix_assignment === false;

  const lastLoggedPcbaPrefixSupportKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const check = pcbaPrefixCheckQuery.data;
    if (
      check == null ||
      (check.matches_required_tla !== false && check.has_prefix_assignment !== false)
    ) {
      if (check?.matches_required_tla !== false && check?.has_prefix_assignment !== false) {
        lastLoggedPcbaPrefixSupportKeyRef.current = null;
      }
      return;
    }

    const supportKey = [
      check.pcba_serial,
      check.required_tla_sku ?? "",
      check.expectation?.expected_tla_sku ?? "",
      String(check.has_prefix_assignment),
      String(check.matches_required_tla),
    ].join("|");
    if (lastLoggedPcbaPrefixSupportKeyRef.current === supportKey) {
      return;
    }
    lastLoggedPcbaPrefixSupportKeyRef.current = supportKey;

    recordConfigurationPcbaPrefixTlaCheckForSupport(check, {
      finishedSku: targetSku,
      batchItemId: mode === "build" && Number.isFinite(batchItemId) ? batchItemId : null,
      alternateOrderId:
        mode === "alternate_order" && Number.isFinite(alternateOrderId) ? alternateOrderId : null,
    });
  }, [
    alternateOrderId,
    batchItemId,
    mode,
    pcbaPrefixCheckQuery.data,
    targetSku,
  ]);

  const clampSerialNeedsQcCheck = useMemo(() => {
    const trimmed = clampSerialDraft.trim();
    return trimmed !== "" && trimmed.toUpperCase() !== CONFIGURATION_US_CLAMP_SCAN_VALUE;
  }, [clampSerialDraft]);

  const clampQcCheckQuery = useQuery({
    queryKey: ["manufacturing", "configuration", "clamp-qc-check", clampSerialDraft] as const,
    queryFn: () => fetchConfigurationClampQcCheck(clampSerialDraft.trim()),
    enabled: clampSerialNeedsQcCheck,
    staleTime: 30_000,
  });

  const openQcIncidentCheckEnabled =
    pcbaSerialDraft.trim() !== "" ||
    clampSerialNeedsQcCheck ||
    deviceLabelSerialDraft.trim() !== "" ||
    (rescanSerialFciId != null && rescanSerialFciId > 0);

  const openQcIncidentCheckQuery = useQuery({
    queryKey: [
      "manufacturing",
      "configuration",
      "open-qc-incident-check",
      pcbaSerialDraft,
      clampSerialDraft,
      deviceLabelSerialDraft,
      rescanSerialFciId,
    ] as const,
    queryFn: () =>
      fetchConfigurationOpenQualityIncidentCheck({
        pcbaSerial: pcbaSerialDraft.trim() === "" ? null : pcbaSerialDraft.trim(),
        clampSerial: clampSerialDraft.trim() === "" ? null : clampSerialDraft.trim(),
        deviceLabelSerial: deviceLabelSerialDraft.trim() === "" ? null : deviceLabelSerialDraft.trim(),
        serialFciId: rescanSerialFciId,
      }),
    enabled: openQcIncidentCheckEnabled,
    staleTime: 15_000,
  });

  const openQcIncidentCheckPending =
    openQcIncidentCheckEnabled &&
    (openQcIncidentCheckQuery.isPending || openQcIncidentCheckQuery.isFetching);
  const openQcIncidentCheckFailed = openQcIncidentCheckEnabled && openQcIncidentCheckQuery.isError;
  const openQcIncidentBlocked =
    openQcIncidentCheckEnabled && openQcIncidentCheckQuery.data?.eligible === false;

  const lastLoggedOpenQcIncidentSupportKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const check = openQcIncidentCheckQuery.data;
    if (check == null || check.skipped || check.eligible !== false) {
      if (check?.eligible !== false) {
        lastLoggedOpenQcIncidentSupportKeyRef.current = null;
      }
      return;
    }

    const supportKey = [
      String(check.incident_id ?? ""),
      check.block_message ?? "",
      pcbaSerialDraft.trim(),
      clampSerialDraft.trim(),
      deviceLabelSerialDraft.trim(),
      String(rescanSerialFciId ?? ""),
    ].join("|");
    if (lastLoggedOpenQcIncidentSupportKeyRef.current === supportKey) {
      return;
    }
    lastLoggedOpenQcIncidentSupportKeyRef.current = supportKey;

    recordConfigurationOpenQualityIncidentCheckForSupport(check, {
      finishedSku: targetSku,
      batchItemId: mode === "build" && Number.isFinite(batchItemId) ? batchItemId : null,
      alternateOrderId:
        mode === "alternate_order" && Number.isFinite(alternateOrderId) ? alternateOrderId : null,
      pcbaSerial: pcbaSerialDraft.trim() === "" ? null : pcbaSerialDraft.trim(),
      clampSerial: clampSerialDraft.trim() === "" ? null : clampSerialDraft.trim(),
      deviceLabelSerial: deviceLabelSerialDraft.trim() === "" ? null : deviceLabelSerialDraft.trim(),
      serialFciId: rescanSerialFciId,
    });
  }, [
    alternateOrderId,
    batchItemId,
    clampSerialDraft,
    deviceLabelSerialDraft,
    mode,
    openQcIncidentCheckQuery.data,
    pcbaSerialDraft,
    rescanSerialFciId,
    targetSku,
  ]);

  const lastLoggedClampQcSupportKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const check = clampQcCheckQuery.data;
    if (check == null || check.skipped || check.eligible !== false) {
      if (check?.eligible !== false) {
        lastLoggedClampQcSupportKeyRef.current = null;
      }
      return;
    }

    const supportKey = [check.clamp_serial, check.block_message ?? "", String(check.eligible)].join(
      "|",
    );
    if (lastLoggedClampQcSupportKeyRef.current === supportKey) {
      return;
    }
    lastLoggedClampQcSupportKeyRef.current = supportKey;

    recordConfigurationClampQcCheckForSupport(check, {
      finishedSku: targetSku,
      batchItemId: mode === "build" && Number.isFinite(batchItemId) ? batchItemId : null,
      alternateOrderId:
        mode === "alternate_order" && Number.isFinite(alternateOrderId) ? alternateOrderId : null,
    });
  }, [
    alternateOrderId,
    batchItemId,
    clampQcCheckQuery.data,
    mode,
    targetSku,
  ]);

  const clampSerialBlocked = clampSerialNeedsQcCheck && clampQcCheckQuery.data?.eligible === false;

  const pcbaTestCheckEnabled =
    polledMcuUuid.trim() !== "" &&
    targetSku.trim() !== "" &&
    !skipsPcbaTestRecordRequirement(targetSku);

  const pcbaTestCheckQuery = useQuery({
    queryKey: [
      "manufacturing",
      "configuration",
      "pcba-test-check",
      polledMcuUuid,
      targetSku,
      pcbaSerialDraft.trim(),
    ] as const,
    queryFn: () =>
      fetchConfigurationPcbaTestCheck({
        mcu_uuid: polledMcuUuid.trim(),
        finished_sku: targetSku.trim(),
        pcba_serial: pcbaSerialDraft.trim() === "" ? null : pcbaSerialDraft.trim(),
      }),
    enabled: pcbaTestCheckEnabled,
    staleTime: 30_000,
  });

  const pcbaTestBlocked =
    pcbaTestCheckEnabled && pcbaTestCheckQuery.data?.eligible === false;
  const pcbaTestMissingRowNotice =
    pcbaTestCheckEnabled &&
    pcbaTestCheckQuery.data?.eligible === true &&
    pcbaTestCheckQuery.data?.missing_row === true;

  const iccidCarrierAlignment = useMemo(
    () => validateConfigurationIccidCarrierAlignment(iccidDraft, targetSku),
    [iccidDraft, targetSku],
  );
  const iccidCarrierBlocked = !iccidCarrierAlignment.isValid;

  const scanBlockedReason = useMemo(() => {
    const parts: string[] = [];
    if (pcbaPrefixNoAssignment) {
      parts.push(
        pcbaPrefixCheckQuery.data?.mismatch_message ??
          configurationPcbaPrefixNoAssignmentMessage(pcbaPrefixCheckQuery.data?.pcba_prefix),
      );
    } else if (pcbaPrefixMismatch) {
      parts.push(
        pcbaPrefixCheckQuery.data?.mismatch_message ??
          "This PCBA batch is not assigned to the TLA required for this job.",
      );
    }
    if (clampSerialBlocked && clampQcCheckQuery.data?.block_message) {
      parts.push(clampQcCheckQuery.data.block_message);
    }
    if (openQcIncidentCheckPending) {
      parts.push("Checking open QC incidents…");
    } else if (openQcIncidentCheckFailed) {
      parts.push("Could not check open QC incidents. Try again before submitting.");
    } else if (openQcIncidentBlocked && openQcIncidentCheckQuery.data?.block_message) {
      parts.push(openQcIncidentCheckQuery.data.block_message);
    }
    if (pcbaTestBlocked && pcbaTestCheckQuery.data?.block_message) {
      parts.push(pcbaTestCheckQuery.data.block_message);
    }
    if (iccidCarrierBlocked && iccidCarrierAlignment.error) {
      parts.push(iccidCarrierAlignment.error);
    }
    return parts.join(" ");
  }, [
    clampQcCheckQuery.data?.block_message,
    clampSerialBlocked,
    openQcIncidentBlocked,
    openQcIncidentCheckFailed,
    openQcIncidentCheckPending,
    openQcIncidentCheckQuery.data?.block_message,
    iccidCarrierAlignment.error,
    iccidCarrierBlocked,
    pcbaTestBlocked,
    pcbaTestCheckQuery.data?.block_message,
    pcbaPrefixCheckQuery.data?.mismatch_message,
    pcbaPrefixCheckQuery.data?.pcba_prefix,
    pcbaPrefixCheckQuery.data?.required_tla_sku,
    pcbaPrefixMismatch,
    pcbaPrefixNoAssignment,
    requiredTlaSku,
  ]);

  const showBatchProgress = mode === "build" || mode === "alternate_order";
  const remainingToBuild = showBatchProgress
    ? jobProgressQuery.data?.remainingToBuild ?? activeQueueItem?.remainingToBuild ?? null
    : null;
  const batchComplete =
    showBatchProgress &&
    remainingToBuild != null &&
    remainingToBuild <= 0 &&
    rescanSerialFciId == null;
  const batchCapBlockedReason = batchComplete
    ? "This job is complete (0 remaining). Another operator may have finished the last unit, or wait for a refresh if a unit was removed."
    : "";

  const viewerKnown = viewerUserId !== null && viewerUserId !== "";
  const showOtherOperatorsOnJob =
    operatorsOnJob.length > 0 &&
    (!viewerKnown || operatorsOnJob.some((operator) => operator.userId !== viewerUserId));
  const operatorsOnJobForStack = useMemo(() => operatorsOnJob, [operatorsOnJob]);

  const builtUnits = builtUnitsQuery.data?.units ?? [];

  const matchingBuiltUnit = useMemo(() => {
    if (!builtUnitsQuery.isSuccess || polledMcuUuid.trim() === "") return null;
    return builtUnits.find((unit) => configurationMcuUuidsMatch(unit.mcuUuid, polledMcuUuid)) ?? null;
  }, [builtUnits, builtUnitsQuery.isSuccess, polledMcuUuid]);

  const rescanBuiltUnit = useMemo(() => {
    if (rescanSerialFciId == null) return null;
    return builtUnits.find((unit) => unit.serialFciId === rescanSerialFciId) ?? null;
  }, [builtUnits, rescanSerialFciId]);

  const mcuAlreadyConfiguredBlockReason = useMemo(() => {
    if (!showBatchProgress || matchingBuiltUnit == null) return "";
    if (rescanSerialFciId === matchingBuiltUnit.serialFciId) return "";
    if (submitResult != null && submitResult.serialFciId === matchingBuiltUnit.serialFciId) return "";
    return CONFIGURATION_ALREADY_ON_ORDER_BUTTON_LABEL;
  }, [matchingBuiltUnit, polledMcuUuid, rescanSerialFciId, showBatchProgress, submitResult]);

  const extraSubmitBlockedReason = useMemo(() => {
    if (submitResult != null) return "";
    return batchCapBlockedReason || mcuAlreadyConfiguredBlockReason;
  }, [submitResult, batchCapBlockedReason, mcuAlreadyConfiguredBlockReason]);

  const submitGateReason = useMemo(() => {
    if (submitResult != null) return "";
    const parts: string[] = [];
    if (scanBlockedReason.trim() !== "") parts.push(scanBlockedReason.trim());
    if (extraSubmitBlockedReason.trim() !== "") parts.push(extraSubmitBlockedReason.trim());
    return parts.join(" ");
  }, [extraSubmitBlockedReason, scanBlockedReason, submitResult]);

  const submitGateFieldHighlights = useMemo(() => {
    const highlights: Record<string, boolean> = {};
    if (pcbaPrefixMismatch || pcbaPrefixNoAssignment) {
      highlights.pcbaSerial = true;
    }
    if (clampSerialBlocked) {
      highlights.clampSerial = true;
    }
    if (openQcIncidentBlocked) {
      const message = openQcIncidentCheckQuery.data?.block_message ?? "";
      if (message.includes(" on PCBA ")) {
        highlights.pcbaSerial = true;
      } else if (message.includes(" on clamp ")) {
        highlights.clampSerial = true;
      } else if (message.includes(" on device ")) {
        highlights.deviceLabelSerial = true;
      }
    }
    if (iccidCarrierBlocked) {
      highlights.iccid = true;
    }
    return highlights;
  }, [
    clampSerialBlocked,
    iccidCarrierBlocked,
    openQcIncidentBlocked,
    openQcIncidentCheckQuery.data?.block_message,
    pcbaPrefixMismatch,
    pcbaPrefixNoAssignment,
  ]);

  const handleOpenPreConfigQcIncident = useCallback(() => {
    const form = configurationFormRef.current;
    const pick = (formValue: string, deviceValue: string): string => {
      const fromForm = formValue.trim();
      if (fromForm !== "") return fromForm;
      return deviceValue.trim();
    };
    setPreConfigQcPcbaSerial(
      pick(form?.readFieldValue("pcbaSerial") ?? "", readConfigurationPrefillValue("pcbaSerial", activeDevice)),
    );
    setPreConfigQcIdentity({
      deviceLabelSerial: pick(
        form?.readFieldValue("deviceLabelSerial") ?? "",
        readConfigurationPrefillValue("deviceLabelSerial", activeDevice),
      ),
      clampSerial: pick(
        form?.readFieldValue("clampSerial") ?? "",
        readConfigurationPrefillValue("clampSerial", activeDevice),
      ),
      mcuUuid: readPolledDeviceMcuUuid(activeDevice).trim(),
    });
    setPreConfigQcOpen(true);
  }, [activeDevice]);

  const focusConfigurationScanner = useCallback((): void => {
    window.requestAnimationFrame(() => {
      configurationFormRef.current?.focusScanner();
    });
  }, []);

  const preConfigQcIssueNotes = useMemo(
    () =>
      configurationPreConfigQcIssueNotes({
        batchItemId: activeQueueItem?.batchItemId ?? null,
        alternateOrderId: activeQueueItem?.alternateOrderId ?? null,
      }),
    [activeQueueItem?.alternateOrderId, activeQueueItem?.batchItemId],
  );

  const preConfigQcProductFamily = useMemo(
    () => inferPcbaProductFamilyFromConfigureSku(targetSku),
    [targetSku],
  );

  const preConfigQcTlaVariant = useMemo(
    () => inferPcbaTlaVariantFromTlaSku(requiredTlaSku),
    [requiredTlaSku],
  );

  const handleRescan = useCallback(
    (unit: ConfigurationBuiltUnit) => {
      setReconfigureError(null);
      const rowMcu = unit.mcuUuid?.trim() ?? "";
      if (rowMcu === "") {
        setReconfigureError(`serial_fci ${unit.serialFciId} has no MCU UUID on record; rescan is not available.`);
        return;
      }
      if (activeCount !== 1) {
        setReconfigureError(
          activeCount === 0
            ? "Connect exactly one device before rescanning."
            : `Connect exactly one device before rescanning (${activeCount} connected).`,
        );
        return;
      }
      const polledMcu = readPolledDeviceMcuUuid(activeDevice);
      if (polledMcu.trim() === "") {
        setReconfigureError("MCU UUID is not available from SDTestApp — plug the unit in and wait for demographics.");
        return;
      }
      if (!configurationMcuUuidsMatch(polledMcu, rowMcu)) {
        setReconfigureError(`Plug in this unit (MCU UUID ${rowMcu}) — the connected device reports ${polledMcu}.`);
        return;
      }
      setRescanSerialFciId(unit.serialFciId);
      configurationFormRef.current?.prefillForRescan(unit);
      setPcbaSerialDraft(unit.pcbaSerial ?? "");
      setClampSerialDraft(unit.clampSerial ?? "");
    },
    [activeCount, activeDevice],
  );

  useEffect(() => {
    if (!activeDevice) return;
    const timer = setTimeout(() => {
      refreshPcbaSerialDraft();
      refreshClampSerialDraft();
    }, 0);
    return () => clearTimeout(timer);
  }, [activeDevice, refreshClampSerialDraft, refreshPcbaSerialDraft]);

  useEffect(() => {
    return () => {
      if (connectorRefreshNoticeTimeoutRef.current) {
        clearTimeout(connectorRefreshNoticeTimeoutRef.current);
      }
    };
  }, []);

  const showConnectorRefreshNotice = useCallback(
    (notice: { text: string; tone: "error" | "warning" | "success" }, autoClearMs?: number) => {
      if (connectorRefreshNoticeTimeoutRef.current) {
        clearTimeout(connectorRefreshNoticeTimeoutRef.current);
        connectorRefreshNoticeTimeoutRef.current = null;
      }
      setConnectorRefreshNotice(notice);
      if (autoClearMs != null && autoClearMs > 0 && notice.tone === "success") {
        connectorRefreshNoticeTimeoutRef.current = setTimeout(() => {
          setConnectorRefreshNotice(null);
          connectorRefreshNoticeTimeoutRef.current = null;
        }, autoClearMs);
      }
    },
    [],
  );

  const refreshConnectorSession = useCallback(
    async (connectorId: string, options?: { silent?: boolean }): Promise<boolean> => {
      const client = httpClientRef.current;
      const trimmedConnectorId = connectorId.trim();
      if (!client || trimmedConnectorId === "") {
        if (!options?.silent) {
          showConnectorRefreshNotice({
            text: "Connect exactly one device before refreshing the connector.",
            tone: "error",
          });
        }
        return false;
      }

      if (!options?.silent) {
        setConnectorRefreshPending(true);
        showConnectorRefreshNotice({ text: "Refreshing connector…", tone: "success" });
        configurationFormRef.current?.clearSubmitErrors();
      }

      try {
        const result = await client.refreshConnector(trimmedConnectorId);
        setDemographics(result.demographics);
        if (!options?.silent) {
          if (result.warning) {
            showConnectorRefreshNotice({ text: result.warning, tone: "warning" });
          } else {
            showConnectorRefreshNotice({ text: "Device refreshed.", tone: "success" }, 4000);
          }
        }
        return true;
      } catch (error) {
        if (!options?.silent) {
          const message =
            error instanceof SdTestConnectorRefreshError
              ? error.message
              : error instanceof Error
                ? error.message
                : "Device refresh failed.";
          showConnectorRefreshNotice({ text: message, tone: "error" });
        }
        return false;
      } finally {
        if (!options?.silent) {
          setConnectorRefreshPending(false);
        }
      }
    },
    [showConnectorRefreshNotice],
  );

  const handleRefreshConnector = useCallback(() => {
    void (async () => {
      if (connection !== "connected") {
        showConnectorRefreshNotice({
          text: "SDTestApp is offline — use Reconnect on the SDTestApp panel.",
          tone: "error",
        });
        return;
      }

      let connectorId = connectedConnectorId.trim() !== "" ? connectedConnectorId : lastConnectorIdRef.current.trim();
      if (connectorId === "") {
        setConnectorRefreshPending(true);
        showConnectorRefreshNotice({ text: "Scanning for cable…", tone: "success" });
        configurationFormRef.current?.clearSubmitErrors();
        try {
          const client = httpClientRef.current;
          if (client) {
            await client.refresh();
            const devices = client.getActiveDevices();
            if (devices.length === 1) {
              connectorId = readDeviceConnectorId(devices[0]);
            } else if (devices.length > 1) {
              showConnectorRefreshNotice({
                text: `Connect exactly one device before refreshing (${devices.length} connected).`,
                tone: "error",
              });
              return;
            }
          }
        } finally {
          setConnectorRefreshPending(false);
        }
        if (connectorId === "") {
          showConnectorRefreshNotice({
            text: "No cable detected. Plug in the device and check the USB connection, or confirm SDTestApp sees the scanner.",
            tone: "error",
          });
          return;
        }
      }

      await refreshConnectorSession(connectorId);
    })();
  }, [connectedConnectorId, connection, refreshConnectorSession, showConnectorRefreshNotice]);

  const handleFormSubmit = useCallback(
    async (formData: Record<string, string>) => {
      setSubmitResult(null);

      const client = httpClientRef.current;
      if (!client) {
        throw new Error("SDTestApp client is not ready.");
      }

      const device = client.getActiveDevices()[0];
      const connectorId = String(device?.connectorId ?? device?.cableSerialNumber ?? device?.serialNumber ?? "");
      if (connectorId === "") {
        throw new Error("No connected device found. Connect exactly one device to SDTestApp.");
      }
      if (targetSku === "") {
        throw new Error("Select a target SKU first.");
      }

      if (skuRequiresImeiIccid(targetSku)) {
        const iccidFromForm = (formData.iccid ?? "").trim();
        const carrierAlignment = validateConfigurationIccidCarrierAlignment(iccidFromForm, targetSku);
        if (!carrierAlignment.isValid && carrierAlignment.error) {
          throw new Error(carrierAlignment.error);
        }
      }

      setIsSubmitting(true);
      try {
        if (jobProgressEnabled && rescanSerialFciId == null) {
          const liveProgress =
            mode === "build"
              ? await fetchConfigurationJobProgress({ batchItemId })
              : await fetchConfigurationJobProgress({ alternateOrderId });
          queryClient.setQueryData(jobProgressQueryKey, liveProgress);
          if (liveProgress.remainingToBuild <= 0) {
            throw new Error(
              "This job is complete (0 remaining). Another operator may have taken the last slot.",
            );
          }
        }

        let deviceBeforeConfigure = device;
        if (isPolledDeviceDemographicsStale(deviceBeforeConfigure)) {
          const refreshed = await refreshConnectorSession(connectorId, { silent: true });
          if (!refreshed) {
            throw new Error(
              "Polled device data looks stale. Use Refresh on the cable row, then submit again.",
            );
          }
          deviceBeforeConfigure = client.getActiveDevices()[0] ?? deviceBeforeConfigure;
        }

        const wirelessConfigure = skuRequiresImeiIccid(targetSku);
        let configureResult;
        try {
          configureResult = await client.configureAndWait(
            connectorId,
            targetSku,
            wirelessConfigure ? 180000 : 60000,
            wirelessConfigure,
          );
        } catch (configureError) {
          if (
            configureError instanceof SdTestAppHubError &&
            (configureError.code === "wireless_portal_check_in_failed" ||
              configureError.code === "wireless_portal_check_in_timeout")
          ) {
            throw new ConfigurationSubmitFailure(
              CONFIGURATION_CHECKIN_FAILED_BUTTON_LABEL,
              configureError.message,
            );
          }
          const message =
            configureError instanceof Error ? configureError.message : "Configuration failed.";
          const communicationDetail = deviceCommunicationFailureDetail(message);
          if (communicationDetail) {
            const operatorMessage = deviceCommunicationFailureMessage(communicationDetail);
            recordConfigurationDeviceCommunicationFailureForSupport({
              operatorMessage,
              rawMessage: message,
              connectorId,
              firmware: readPolledDeviceFirmware(deviceBeforeConfigure),
              finishedSku: targetSku,
              batchItemId: mode === "build" ? batchItemId : null,
              alternateOrderId: mode === "alternate_order" ? alternateOrderId : null,
            });
            throw new Error(operatorMessage);
          }
          if (configureErrorSuggestsConnectorRefresh(message)) {
            throw new Error(`${message} Use Refresh on the cable row, then submit again.`);
          }
          throw configureError instanceof Error ? configureError : new Error(message);
        }
        if (wirelessConfigure) {
          const configurePayload =
            configureResult.response && typeof configureResult.response === "object"
              ? (configureResult.response as Record<string, unknown>)
              : {};
          const results =
            configurePayload.results && typeof configurePayload.results === "object"
              ? (configurePayload.results as Record<string, unknown>)
              : {};
          const connectorResult = results[connectorId];
          const embeddedCheckIn =
            connectorResult &&
            typeof connectorResult === "object" &&
            (connectorResult as Record<string, unknown>).wirelessCheckIn &&
            typeof (connectorResult as Record<string, unknown>).wirelessCheckIn === "object"
              ? ((connectorResult as Record<string, unknown>).wirelessCheckIn as Record<string, unknown>)
              : null;
          if (embeddedCheckIn?.success !== true) {
            try {
              await client.wirelessCellCheckIn(connectorId);
            } catch (checkInError) {
              const message =
                checkInError instanceof Error
                  ? checkInError.message
                  : "Wireless cellular check-in did not complete.";
              throw new ConfigurationSubmitFailure(
                CONFIGURATION_CHECKIN_FAILED_BUTTON_LABEL,
                message,
              );
            }
          }
        }
        const deviceAfterConfigure = client.getActiveDevices()[0] ?? deviceBeforeConfigure;

        const imeiKeyList = SDTEST_FIELD_KEYS.imei;
        const imeiResolved = resolveConfigureSubmitField(
          formData.imei,
          deviceAfterConfigure,
          deviceBeforeConfigure,
          imeiKeyList,
        );
        const imeiAfterConfigure = resolveConfigureSubmitField("", deviceAfterConfigure, null, imeiKeyList);
        const imeiForWireless = imeiAfterConfigure || imeiResolved;

        const iccidResolved = resolveConfigureSubmitField(
          formData.iccid,
          deviceAfterConfigure,
          deviceBeforeConfigure,
          SDTEST_FIELD_KEYS.iccid,
        );

        if (skuRequiresImeiIccid(targetSku)) {
          const missing: string[] = [];
          if (!isValidImeiForWireless(imeiForWireless)) missing.push("IMEI");
          if (!iccidResolved) missing.push("ICCID");
          if (missing.length > 0) {
            throw new Error(
              `This device requires ${missing.join(" and ")} after configuration (device demographics). IMEI cannot be empty or all zeros.`,
            );
          }
        }

        const pcbaSerialResolved = resolveConfigureSubmitField(
          formData.pcbaSerial,
          deviceAfterConfigure,
          deviceBeforeConfigure,
          SDTEST_FIELD_KEYS.pcbaSerial,
        );
        const clampSerialResolved = resolveConfigureSubmitField(
          formData.clampSerial,
          deviceAfterConfigure,
          deviceBeforeConfigure,
          SDTEST_FIELD_KEYS.clampSerial,
        );
        const deviceLabelSerialResolved = resolveConfigureSubmitField(
          formData.deviceLabelSerial,
          deviceAfterConfigure,
          deviceBeforeConfigure,
          SDTEST_FIELD_KEYS.deviceLabelSerial,
        );

        const mcuUuid = extractMcuId(configureResult.response, deviceAfterConfigure);
        if (firmwareSupportsMcuUuid(configureResult.firmware) && mcuUuid.trim() === "") {
          const operatorMessage = missingMcuUuidWhenSupportedMessage(configureResult.firmware);
          recordConfigurationDeviceCommunicationFailureForSupport({
            operatorMessage,
            rawMessage: MCU_UUID_UNREAD_DETAIL,
            connectorId,
            firmware: configureResult.firmware,
            finishedSku: targetSku,
            batchItemId: mode === "build" ? batchItemId : null,
            alternateOrderId: mode === "alternate_order" ? alternateOrderId : null,
          });
          throw new Error(operatorMessage);
        }

        const submitBody: SubmitConfigurationBody = {
          finishedSku: targetSku,
          firmware: configureResult.firmware,
          mcuUuid: mcuUuid || null,
          pcbaSerial: pcbaSerialResolved || null,
          clampSerial: clampSerialResolved || null,
          deviceLabelSerial: deviceLabelSerialResolved,
          childPcba: null,
          imei: skuRequiresImeiIccid(targetSku) ? imeiForWireless : imeiResolved || null,
          iccid: iccidResolved || null,
          batchItemId: mode === "build" ? activeQueueItem?.batchItemId ?? batchItemId : null,
          buildOrderNumber: mode === "build" ? activeQueueItem?.buildOrderNumber ?? null : null,
          isAlternate: mode === "alternate" || mode === "alternate_order",
          alternateOrderId: mode === "alternate_order" ? alternateOrderId : null,
          rescanSerialFciId,
        };

        if (skuRequiresImeiIccid(targetSku) && iccidResolved) {
          const wirelessCarrier = resolveWirelessSimCarrier(iccidResolved, targetSku);
          if (!wirelessCarrier) {
            throw new Error(
              "Could not determine SIM carrier from ICCID or finished SKU. Load the ICCID in SIM inventory or verify the FI-5A-W carrier digit.",
            );
          }
          showToast({
            tone: "info",
            message: `Checking ${wirelessCarrier} SIM activation — if this ICCID is new, carrier activation may take a moment.`,
          });
        }

        const result = await submitConfiguration(submitBody);
        setSubmitResult(result);
        setRescanSerialFciId(null);
        playPairSuccessSound();
        try {
          await labor.recordScan({
            deviceSerial: result.deviceLabelSerial,
            target: { kind: "serial_fci", id: result.serialFciId },
          });
        } catch {
          // Labor scan is best-effort; the configuration write already succeeded.
        }
        void jobProgressQuery.refetch();
        void builtUnitsQuery.refetch();
      } catch (error) {
        if (error instanceof ApiError || error instanceof ConfigurationSubmitFailure) {
          throw error;
        }
        throw error instanceof Error ? error : new Error("Submit failed.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      alternateOrderId,
      batchItemId,
      builtUnitsQuery,
      jobProgressEnabled,
      jobProgressQueryKey,
      jobProgressQuery,
      labor,
      mode,
      queryClient,
      activeQueueItem,
      batchItemId,
      rescanSerialFciId,
      refreshConnectorSession,
      targetSku,
    ],
  );

  const workTitle =
    mode === "build"
      ? activeQueueItem
        ? `Configure ${activeQueueItem.sku} · batch ${activeQueueItem.batchId}`
        : "Configure build line"
      : mode === "alternate_order"
        ? activeQueueItem
          ? `Configure ${activeQueueItem.sku} · alternate order ${activeQueueItem.alternateOrderId}`
          : "Configure alternate order"
        : `Configure inventory alternate · ${alternateSku}`;

  const jobContextMissing =
    queueQuery.isSuccess &&
    queueItem == null &&
    cachedQueueItemRef.current == null &&
    jobProgressEnabled &&
    !jobProgressQuery.isLoading &&
    !jobProgressQuery.isFetching &&
    jobProgressQuery.data == null;

  const showInitialJobLoading =
    (mode === "build" || mode === "alternate_order") &&
    !jobContextReadyRef.current &&
    (queueQuery.isPending || jobContextMissing);

  const preConfigQcModal = (
    <ConfigurationPreConfigQcIncidentModal
      open={preConfigQcOpen}
      onClose={() => setPreConfigQcOpen(false)}
      onSubmitted={() => {
        void queryClient.invalidateQueries({
          queryKey: ["manufacturing", "configuration", "open-qc-incident-check"],
        });
        focusConfigurationScanner();
      }}
      pcbaSerialPrefill={preConfigQcPcbaSerial}
      defaultPcbaProductFamily={preConfigQcProductFamily}
      defaultPcbaTlaVariant={preConfigQcTlaVariant}
      defaultIssueNotes={preConfigQcIssueNotes}
      targetSku={targetSku}
      identityPrefill={preConfigQcIdentity}
    />
  );

  if (mode === "alternate" && alternateSku === "") {
    return (
      <>
        <p className="text-sm text-slate-500">
          <Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Redirecting to queue...
        </p>
        {preConfigQcModal}
      </>
    );
  }

  if (showInitialJobLoading) {
    return (
      <>
        <p className="text-sm text-slate-500">
          <Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Loading job...
        </p>
        {preConfigQcModal}
      </>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <PageTitleWithHelp hoverHint={WORK_PAGE_HELP}>{workTitle}</PageTitleWithHelp>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-2">
          <DeviceLookupModalTrigger
            onApplyToConfiguration={(serials) => {
              configurationFormRef.current?.applyLookupSerials(serials);
              if (serials.pcbaSerial != null && serials.pcbaSerial.trim() !== "") {
                setPcbaSerialDraft(serials.pcbaSerial.trim());
              }
              if (serials.clampSerial != null && serials.clampSerial.trim() !== "") {
                setClampSerialDraft(serials.clampSerial.trim());
              }
            }}
          />
          {showOtherOperatorsOnJob ? (
            <div className="flex flex-col items-end gap-0.5">
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                {viewerKnown ? "Other operators on this job" : "Operators on this job"}
              </span>
              <PrePackCartonOperatorStack
                operators={operatorsOnJobForStack}
                viewerUserId={viewerUserId}
                omitViewer={viewerKnown}
              />
            </div>
          ) : null}
          {showBatchProgress && remainingToBuild != null ? (
            <p className="pt-1 text-sm font-semibold text-slate-900">{formatKitQuantity(remainingToBuild)} remaining</p>
          ) : null}
        </div>
      </div>

      {!writesAllowed ? (
        <Card>
          <CardBody>
            <p className="text-sm text-amber-700">
              Your role is read-only for Manufacturing. You can connect to SDTestApp and fill the form, but submitting
              is disabled.
            </p>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardBody className="p-0">
          <div className="overflow-hidden sm:flex sm:items-stretch">
            <div className="min-w-0 flex-1 bg-white p-4">
              <div className="space-y-4">
              {targetSku !== "" ? (
                <ConfigurationJobTargets
                  targetSku={targetSku}
                  requiredTlaSku={requiredTlaSku}
                  requiredTlaLoading={requiredTlaQuery.isLoading}
                  showClearForm={deviceType !== ""}
                  onClearForm={() => {
                    setRescanSerialFciId(null);
                    setReconfigureError(null);
                    configurationFormRef.current?.clearFormAndFocus();
                  }}
                  showPreConfigQcIncident={deviceType !== ""}
                  preConfigQcDisabled={!writesAllowed}
                  onPreConfigQcIncident={handleOpenPreConfigQcIncident}
                />
              ) : null}

              <ConfigurationDeviceForm
                ref={configurationFormRef}
                deviceType={deviceType}
                activeDevice={activeDevice}
                connection={connection}
                activeDeviceCount={activeCount}
                sdTestStatusText={status.text}
                writesAllowed={writesAllowed}
                isSubmitting={isSubmitting}
                submitGateFieldHighlights={submitGateFieldHighlights}
                submitGateReason={submitGateReason}
                submitSuccessMessage={submitSuccessMessage}
                rescanArmed={rescanSerialFciId != null}
                rescanDeviceLabel={rescanBuiltUnit?.deviceLabelSerial ?? null}
                onFormCleared={() => {
                  setRescanSerialFciId(null);
                  setPcbaSerialDraft("");
                  setClampSerialDraft("");
                  setDeviceLabelSerialDraft("");
                  setIccidDraft("");
                }}
                onScannerInput={() => {
                  setSubmitResult(null);
                  refreshPcbaSerialDraft();
                  refreshClampSerialDraft();
                  refreshDeviceLabelSerialDraft();
                  refreshIccidDraft();
                }}
                onFieldChange={(fieldName, value) => {
                  if (fieldName === "pcbaSerial") {
                    setPcbaSerialDraft(value);
                  }
                  if (fieldName === "clampSerial") {
                    setClampSerialDraft(value);
                  }
                  if (fieldName === "deviceLabelSerial") {
                    setDeviceLabelSerialDraft(value);
                  }
                  if (fieldName === "iccid") {
                    setIccidDraft(value);
                  }
                }}
                finishedSku={targetSku}
                configureSupportLogContext={{
                  finishedSku: targetSku,
                  batchItemId: mode === "build" && Number.isFinite(batchItemId) ? batchItemId : null,
                  alternateOrderId:
                    mode === "alternate_order" && Number.isFinite(alternateOrderId)
                      ? alternateOrderId
                      : null,
                  rescanSerialFciId,
                  mcuUuid: polledMcuUuid || null,
                }}
                onSubmit={handleFormSubmit}
              />
              </div>
            </div>

            <aside className="w-full shrink-0 space-y-4 border-t border-slate-200 bg-slate-50 p-4 sm:w-64 sm:border-l sm:border-t-0">
              <ConfigurationSdTestConnectionPanel
                port={port}
                onPortChange={setPort}
                status={status}
                connection={connection}
                activeCount={activeCount}
                onReconnect={() => {
                  configurationFormRef.current?.clearSubmitErrors();
                  void httpClientRef.current?.refresh();
                }}
              />
              <div className="border-t border-slate-200 pt-4">
                <ConfigurationDevicePolledPanel
                  activeDevice={activeDevice}
                  showImei={skuRequiresImeiIccid(targetSku)}
                  connectorId={displayedConnectorId}
                  refreshPending={connectorRefreshPending}
                  refreshNotice={connectorRefreshNotice}
                  onRefreshConnector={handleRefreshConnector}
                  refreshEnabled={connection === "connected"}
                  refreshDisabled={isSubmitting}
                />
                {pcbaTestMissingRowNotice ? (
                  <p className="mt-3 text-sm text-slate-600">
                    No PCBA test record for this MCU; one will be added when you submit.
                  </p>
                ) : null}
                <ConfigurationPcbaTestList tests={pcbaTestCheckQuery.data?.tests ?? []} />
              </div>
            </aside>
          </div>
        </CardBody>
      </Card>

      {showBatchProgress ? (
        <Card>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Configured on this job</CardTitle>
            {!builtUnitsQuery.isLoading &&
            !builtUnitsQuery.isError &&
            (builtUnitsQuery.data?.units.length ?? 0) > 0 ? (
              <Input
                id="configuration-built-units-filter"
                value={builtUnitsFilter}
                placeholder="Filter device label, PCBA, clamp, MCU UUID, serial_fci…"
                aria-label="Filter configured units"
                className="h-10 w-full sm:max-w-md"
                onChange={(event) => setBuiltUnitsFilter(event.target.value)}
              />
            ) : null}
          </CardHeader>
          <CardBody className="p-0">
            <ConfigurationBuiltUnitsPanel
              units={builtUnitsQuery.data?.units ?? []}
              loading={builtUnitsQuery.isLoading}
              errorMessage={
                builtUnitsQuery.isError
                  ? builtUnitsQuery.error instanceof Error
                    ? builtUnitsQuery.error.message
                    : "Could not load configured units."
                  : null
              }
              writesAllowed={writesAllowed}
              reconfigureError={reconfigureError}
              filterText={builtUnitsFilter}
              rescanSerialFciId={rescanSerialFciId}
              onRescan={handleRescan}
            />
          </CardBody>
        </Card>
      ) : null}

      {preConfigQcModal}
    </div>
  );
}
